import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkRegular, CopyRegular } from '@fluentui/react-icons';
import type { FieldRow } from './types';

/**
 * Identifier list — like FieldList, but renders values as monospaced pills
 * with a copy-to-clipboard affordance. Used for the Identifiers card on the
 * client detail page (EIN / sales tax certificate / FL business partner # /
 * reemployment tax # / corporate document #).
 *
 * The copy affordance silently no-ops on Clipboard API failure (non-secure
 * contexts, browsers without permission) so the page doesn't crash.
 */
const useStyles = makeStyles({
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, max-content) 1fr',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalS,
    alignItems: 'baseline',
  },
  fieldLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  identifierTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 4px 3px 10px',
    borderRadius: '6px',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
    transition: 'background-color 100ms ease-in',
  },
  identifierTagCopied: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  copyButton: {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: '0',
    border: 'none',
    background: 'transparent',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 100ms ease-in, background-color 100ms ease-in',
    '&:hover': {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
});

export function IdentifierList({ rows, dash }: { rows: FieldRow[]; dash: string }) {
  const styles = useStyles();
  const visible = rows.filter(r => r.value && r.value.toString().trim().length > 0);
  if (visible.length === 0) {
    return <Text className={styles.emptyHint}>{dash}</Text>;
  }
  return (
    <div className={styles.fieldGrid}>
      {visible.map(row => (
        <IdentifierPair key={row.label} label={row.label} value={row.value!} />
      ))}
    </div>
  );
}

function IdentifierPair({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  return (
    <>
      <Text className={styles.fieldLabel}>{label}</Text>
      <span><CopyableIdentifier value={value} /></span>
    </>
  );
}

function CopyableIdentifier({ value }: { value: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    // Fire-and-forget — clipboard write is async but we don't await it inside the
    // event handler so the eslint no-misused-promises rule stays happy.
    navigator.clipboard.writeText(value).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      // Clipboard API can fail in non-secure contexts. Silently no-op so the page doesn't crash.
      () => { /* swallow */ },
    );
  }

  return (
    <span className={`${styles.identifierTag} ${copied ? styles.identifierTagCopied : ''}`}>
      {value}
      <button
        type="button"
        className={styles.copyButton}
        onClick={handleCopy}
        title={copied ? t('identifierList.copied') : t('identifierList.copy')}
        aria-label={t('identifierList.copyValue', { value })}
      >
        {copied ? <CheckmarkRegular fontSize={14} /> : <CopyRegular fontSize={14} />}
      </button>
    </span>
  );
}
